import h2NotesCopy from "@/messages/mandarin-h2-notes-controls.json";
import type { Locale } from "@/lib/i18n/locales";
import rolloutCopy from "@/messages/mandarin-rollout.json";
import Link from "next/link";
import type { Route } from "next";
import Image from "next/image";
import clsx from "clsx";
import { Fragment, useId } from "react";
import type { ReactNode } from "react";
import { BookOpen, PlayCircle, Target } from "lucide-react";

import { pillStyle } from "@/lib/colour";
import { postUrl } from "@/lib/post-url";
import { normalizeThumbnail } from "@/lib/tina/post-helpers";
import { buildSearchExplanationTerms } from "@/lib/blog/search-lexical";
import type { SearchResultExplanation } from "@/lib/blog/search-types";
import SearchResultCardLink from "@/app/components/SearchResultCardLink";
import type { H2StudyRoute } from "@/lib/study/h2-study-types";
import {
  buildSnippetWindow,
  escapeRegex,
  selectSnippetSource,
} from "@/app/components/post-grid-search";

export interface CategoryRef {
  label: string;
  slug: string;
  color?: string | null;
}

export interface PostNode {
  _sys: { filename: string };
  title?: string | null;
  description?: string | null;
  textIndex?: string | null;
  textSnippet?: string | null;
  thumbnail?: string | null;
  category?: CategoryRef | null;
  // Optional academic level facet for experiments hubs
  // Allowed values: 'upper-sec' | 'h2' | 'mixed' (other strings treated as "Mixed")
  level?: "upper-sec" | "h2" | "mixed" | string | null;
}

export interface PostContextLabel {
  label: string;
  detail?: string;
}

type SearchAnalyticsMeta = {
  queryLength: number;
  resultOffset?: number;
  searchBucket?: string;
};

type TuitionGuidePathway = {
  id: "choose" | "plan" | "practical";
  title: string;
  description: string;
  posts: PostNode[];
};

const TUITION_GUIDE_PATHWAYS = [
  {
    id: "choose",
    title: "Compare school and tuition options",
    description:
      "See what to expect at different schools, then compare tuition formats, fees, and support.",
  },
  {
    id: "plan",
    title: "Plan the school years",
    description: "See when to start and what steady progress can look like.",
  },
  {
    id: "practical",
    title: "Prepare for practical work",
    description:
      "Build the laboratory and exam skills needed for practical papers.",
  },
] as const;

function getTuitionGuidePathwayId(post: PostNode): TuitionGuidePathway["id"] {
  const searchableText = `${post.title ?? ""} ${
    post.description ?? ""
  }`.toLowerCase();

  if (
    /\b(fee|fees|budget|cost|class format|comparison|compare|choose|choosing|checklist|worth it|master guide|parent guide|student guide)\b/.test(
      searchableText
    )
  ) {
    return "choose";
  }

  if (
    /\b(jc1|jc2|sec 3|sec 4|roadmap|study plan|revision plan|month-by-month|year-by-year|grade jump|timeline)\b/.test(
      searchableText
    )
  ) {
    return "plan";
  }

  if (
    /\b(practical|paper 3|paper 4|paper 5|laboratory|lab |experiment)/.test(
      searchableText
    )
  ) {
    return "practical";
  }

  return "choose";
}

export function buildTuitionGuidePathways(
  posts: PostNode[]
): TuitionGuidePathway[] {
  const postsByPathway = new Map<TuitionGuidePathway["id"], PostNode[]>(
    TUITION_GUIDE_PATHWAYS.map((pathway) => [pathway.id, []])
  );

  posts.forEach((post) => {
    postsByPathway.get(getTuitionGuidePathwayId(post))?.push(post);
  });

  return TUITION_GUIDE_PATHWAYS.flatMap((pathway) => {
    const pathwayPosts = postsByPathway.get(pathway.id) ?? [];
    return pathwayPosts.length > 0 ? [{ ...pathway, posts: pathwayPosts }] : [];
  });
}

function TuitionGuidePathways({
  posts,
  locale,
  tuitionPracticalImageSrc,
}: {
  posts: PostNode[];
  locale: Locale;
  tuitionPracticalImageSrc?: string;
}) {
  const headingPrefix = useId();
  const isMandarin = locale === "zh-Hans";
  const copy = rolloutCopy.programmeDiscovery;
  const pathways = buildTuitionGuidePathways(posts).map((pathway) =>
    isMandarin ? { ...pathway, ...copy.pathways[pathway.id] } : pathway
  );

  return (
    <div
      className={clsx(
        "not-prose overflow-hidden rounded-3xl border border-slate-200/80 bg-white",
        pathways.length === 1 && "max-w-3xl"
      )}
    >
      {isMandarin ? (
        <p className="px-5 pt-5 text-sm text-slate-600" lang="zh-Hans">
          {copy.englishContent}
        </p>
      ) : null}
      {pathways.map((pathway, pathwayIndex) => {
        const pathwayText = pathway.posts
          .map((post) => post.title ?? "")
          .join(" ")
          .toLowerCase();
        const fallbackImage = normalizeThumbnail(
          pathway.posts.find((post) =>
            Boolean(normalizeThumbnail(post.thumbnail))
          )?.thumbnail
        );
        // Eclat session photos never illustrate olympiad guides.
        const isOlympiadPathway =
          /olympiad|\b(icho|ipho|apho|spho|scho|sbo|ibo)\b/.test(pathwayText);
        const imageSrc =
          pathway.id === "choose"
            ? "/optimised/pexels-32279024.avif"
            : pathway.id === "plan"
            ? "/optimised/pexels-32001505.avif"
            : isOlympiadPathway
            ? fallbackImage
            : tuitionPracticalImageSrc
            ? tuitionPracticalImageSrc
            : pathwayText.includes("biology")
            ? "/practicals/session-stills/biology-food-test-2026-07-21.jpg"
            : pathwayText.includes("chemistry")
            ? "/practicals/session-stills/chemistry-volumetric-2026-07-21.jpg"
            : pathwayText.includes("physics")
            ? "/practicals/session-stills/physics-circuit-2026-07-21.jpg"
            : fallbackImage;
        const isStandaloneCard = pathway.posts.length === 1;
        const standalonePost = pathway.posts[0];

        if (isStandaloneCard && standalonePost) {
          return (
            <section
              key={pathway.id}
              aria-labelledby={`${headingPrefix}-guides-${pathway.id}`}
              className={clsx(
                "border-t border-slate-200/70 first:border-t-0",
                pathwayIndex % 3 === 0 && "bg-white",
                pathwayIndex % 3 === 1 && "bg-blue-50/35",
                pathwayIndex % 3 === 2 && "bg-slate-50/65"
              )}
            >
              <Link<string>
                href={postUrl(standalonePost) as Route<string>}
                className="group grid gap-7 p-5 no-underline transition-colors hover:bg-blue-50/45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-[-2px] sm:p-7 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:items-center lg:gap-12 lg:px-10 lg:py-11"
              >
                <div
                  className={clsx(
                    "relative aspect-[16/10] overflow-hidden rounded-2xl bg-blue-50",
                    pathwayIndex % 2 === 1 && "lg:order-2"
                  )}
                >
                  {imageSrc ? (
                    <Image
                      src={imageSrc}
                      alt=""
                      fill
                      sizes="(max-width: 1023px) 100vw, 33vw"
                      className="object-cover transition-transform duration-300 group-hover:scale-[1.015]"
                    />
                  ) : (
                    <div
                      className="absolute inset-0 bg-gradient-to-br from-blue-100 via-slate-50 to-amber-50"
                      aria-hidden="true"
                    />
                  )}
                </div>

                <div className={clsx(pathwayIndex % 2 === 1 && "lg:order-1")}>
                  <h3
                    id={`${headingPrefix}-guides-${pathway.id}`}
                    lang={isMandarin ? "en" : undefined}
                    className="m-0 text-2xl font-semibold tracking-[-0.02em] text-slate-950 transition-colors group-hover:text-blue-700 sm:text-3xl"
                  >
                    {standalonePost.title ?? standalonePost._sys.filename}
                  </h3>
                  <p
                    lang={
                      isMandarin && standalonePost.description
                        ? "en"
                        : undefined
                    }
                    className="mt-3 max-w-xl text-base leading-7 text-slate-600"
                  >
                    {standalonePost.description ?? pathway.description}
                  </p>
                </div>
              </Link>
            </section>
          );
        }

        return (
          <section
            key={pathway.id}
            aria-labelledby={`${headingPrefix}-guides-${pathway.id}`}
            className={clsx(
              "grid gap-7 border-t border-slate-200/70 p-5 first:border-t-0 sm:p-7 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:items-center lg:gap-12 lg:px-10 lg:py-11",
              pathwayIndex % 3 === 0 && "bg-white",
              pathwayIndex % 3 === 1 && "bg-blue-50/35",
              pathwayIndex % 3 === 2 && "bg-slate-50/65"
            )}
          >
            <div
              className={clsx(
                "relative aspect-[16/10] overflow-hidden rounded-2xl bg-blue-50",
                pathwayIndex % 2 === 1 && "lg:order-2"
              )}
            >
              {imageSrc ? (
                <Image
                  src={imageSrc}
                  alt=""
                  fill
                  sizes="(max-width: 1023px) 100vw, 33vw"
                  className="object-cover"
                />
              ) : (
                <div
                  className="absolute inset-0 bg-gradient-to-br from-blue-100 via-slate-50 to-amber-50"
                  aria-hidden="true"
                />
              )}
            </div>

            <div className={clsx(pathwayIndex % 2 === 1 && "lg:order-1")}>
              <div>
                <h3
                  id={`${headingPrefix}-guides-${pathway.id}`}
                  className="m-0 text-2xl font-semibold tracking-[-0.02em] text-slate-950 sm:text-3xl"
                >
                  {pathway.title}
                </h3>
                <p className="mt-3 max-w-xl text-base leading-7 text-slate-600">
                  {pathway.description}
                </p>
              </div>

              <ul className="mt-6 border-b border-slate-300/80">
                {pathway.posts.map((post) => (
                  <li
                    key={post._sys.filename}
                    className="border-t border-slate-300/80"
                  >
                    <Link<string>
                      href={postUrl(post) as Route<string>}
                      lang={isMandarin ? "en" : undefined}
                      className="group block py-4 text-lg font-semibold leading-snug text-slate-800 no-underline transition-colors hover:text-blue-700 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-4"
                    >
                      {post.title ?? post._sys.filename}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function renderHighlightedSnippet({
  explanationTerms,
  text,
  queryTerms,
}: {
  explanationTerms?: readonly string[];
  text: string;
  queryTerms: readonly string[];
}) {
  const terms = [
    ...new Set(
      (explanationTerms && explanationTerms.length > 0
        ? explanationTerms
        : queryTerms
      ).map((term) => term.trim().toLowerCase())
    ),
  ]
    .filter((term) => term.length > 1)
    .sort((a, b) => b.length - a.length);
  if (terms.length === 0) return text;

  const termPattern = terms.map((term) => escapeRegex(term)).join("|");
  if (!termPattern) return text;

  const regex = new RegExp(
    `(^|[^a-z0-9])(${termPattern})(?=$|[^a-z0-9])`,
    "gi"
  );
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let matchIndex = 0;
  let match = regex.exec(text);

  while (match) {
    const prefix = match[1] ?? "";
    const token = match[2] ?? "";
    const matchStart = match.index;
    const tokenStart = matchStart + prefix.length;
    const tokenEnd = tokenStart + token.length;

    if (cursor < matchStart) {
      nodes.push(
        <Fragment key={`text-${matchIndex}-pre`}>
          {text.slice(cursor, matchStart)}
        </Fragment>
      );
    }

    if (prefix) {
      nodes.push(
        <Fragment key={`text-${matchIndex}-prefix`}>{prefix}</Fragment>
      );
    }

    nodes.push(
      <mark
        key={`highlight-${matchIndex}`}
        className="rounded-sm bg-amber-200/80 px-0.5 text-slate-900"
      >
        {token}
      </mark>
    );

    cursor = tokenEnd;
    matchIndex += 1;
    match = regex.exec(text);
  }

  if (cursor < text.length) {
    nodes.push(
      <Fragment key={`text-${matchIndex}-tail`}>{text.slice(cursor)}</Fragment>
    );
  }

  return nodes.length > 0 ? nodes : text;
}

export default function PostGrid({
  posts,
  locale = "en",
  animate = false,
  showLevelPill = false,
  hideCategoryPill = false,
  postEyebrows,
  searchExplanations,
  searchQueryTerms = [],
  searchAnalytics,
  editorial = false,
  editorialFolio = false,
  tuitionPracticalImageSrc,
  h2StudyRoutesByFilename,
  prioritizeFirstImage = true,
}: {
  posts: PostNode[];
  locale?: Locale;
  animate?: boolean;
  showLevelPill?: boolean;
  hideCategoryPill?: boolean;
  postEyebrows?: ReadonlyMap<string, string | PostContextLabel>;
  searchExplanations?: ReadonlyMap<string, SearchResultExplanation>;
  searchQueryTerms?: readonly string[];
  searchAnalytics?: SearchAnalyticsMeta;
  /** Uses the larger, quieter card treatment reserved for editorial hub paths. */
  editorial?: boolean;
  /** Uses quiet text-led rows for commercial guide directories. */
  editorialFolio?: boolean;
  /** Page-owned image for the practical pathway; other pathways keep their defaults. */
  tuitionPracticalImageSrc?: string;
  h2StudyRoutesByFilename?: ReadonlyMap<string, H2StudyRoute>;
  /** Set false for grids rendered hidden (later pages kept in the HTML for crawlers). */
  prioritizeFirstImage?: boolean;
}) {
  if (posts.length === 0) {
    return (
      <div
        className="flex min-h-[12rem] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white/70 text-sm text-slate-500"
        aria-live="polite"
      >
        {locale === "zh-Hans"
          ? rolloutCopy.programmeDiscovery.emptyGrid
          : "No posts found."}
      </div>
    );
  }

  if (editorialFolio) {
    return (
      <TuitionGuidePathways
        posts={posts}
        locale={locale}
        tuitionPracticalImageSrc={tuitionPracticalImageSrc}
      />
    );
  }

  const searchExplanationTerms = buildSearchExplanationTerms(searchQueryTerms);

  return (
    <ul
      className={clsx(
        "not-prose grid",
        editorialFolio
          ? "gap-x-10 gap-y-12 sm:grid-cols-2"
          : editorial
          ? "gap-x-8 gap-y-10 sm:grid-cols-2"
          : "gap-8 sm:grid-cols-2 lg:grid-cols-3",
        animate && "animate-fade-in"
      )}
      data-animate={animate ? "true" : undefined}
    >
      {posts.map((post, index) => {
        const isFeatured = prioritizeFirstImage && index === 0;
        const thumbnail = normalizeThumbnail(post.thumbnail);
        const postSearchExplanation = searchExplanations?.get(
          post._sys.filename
        );
        const explanationTerms = postSearchExplanation?.explanationTerms.length
          ? postSearchExplanation.explanationTerms
          : searchExplanationTerms;
        const searchResultRank = searchAnalytics
          ? (searchAnalytics.resultOffset ?? 0) + index + 1
          : null;
        const snippetSelection = searchAnalytics
          ? selectSnippetSource({
              title: post.title?.trim() ?? "",
              description: post.description?.trim() ?? "",
              explanationTerms,
              preferredFields: postSearchExplanation?.matchedFields,
              textIndex: post.textIndex?.trim() ?? "",
              textSnippet: post.textSnippet?.trim() ?? "",
              queryTerms: searchQueryTerms,
            })
          : null;
        const snippetText =
          searchAnalytics && snippetSelection?.snippetSource
            ? buildSnippetWindow({
                explanationTerms,
                text: snippetSelection.snippetSource,
                queryTerms: searchQueryTerms,
              })
            : null;
        const matchedTerms = postSearchExplanation?.matchedTerms.length
          ? postSearchExplanation.matchedTerms.slice(0, 3)
          : snippetSelection?.matchedTerms ?? [];
        const postEyebrow = postEyebrows?.get(post._sys.filename);
        const postContext =
          typeof postEyebrow === "string"
            ? { label: postEyebrow }
            : postEyebrow;
        const cardHref = postUrl(post);
        const h2StudyRoute = h2StudyRoutesByFilename?.get(post._sys.filename);

        if (h2StudyRoute && postContext) {
          const topicNumber = postContext.label.replace(/^Topic\s+/i, "");
          const displayNumber = /^\d$/.test(topicNumber)
            ? topicNumber.padStart(2, "0")
            : topicNumber;
          const topicName = postContext.detail ?? h2StudyRoute.topicLabel;
          const availableResourceCount =
            1 +
            Number(Boolean(h2StudyRoute.quizHref)) +
            Number(Boolean(h2StudyRoute.videoHref));
          const sharedStudyAnalytics = {
            "data-ga-category": "study",
            "data-ga-journey": "h2_study_loop",
            "data-ga-measurement-contract": "h2_study_v1",
            "data-ga-study-subject": h2StudyRoute.subject,
            "data-ga-study-topic": h2StudyRoute.topicId,
          } as const;

          return (
            <li
              key={post._sys.filename}
              className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-[0_24px_55px_rgba(15,23,42,0.12)] focus-within:-translate-y-1 focus-within:border-slate-300 focus-within:shadow-[0_24px_55px_rgba(15,23,42,0.12)]"
              data-ga-event="study_topic_card_open"
              data-ga-manual="true"
              data-ga-impression="true"
              data-ga-impression-event="study_topic_card_visible"
              data-ga-source-surface="h2_topic_card"
              data-ga-result-rank={index + 1}
              data-ga-available-resource-count={availableResourceCount}
              {...sharedStudyAnalytics}
            >
              <Link<string>
                href={h2StudyRoute.noteHref as Route<string>}
                className="block no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                data-ga-event="study_resource_selected"
                data-ga-action="open_topic_notes"
                data-ga-resource-type="notes"
                data-ga-source-surface="h2_topic_card_body"
                data-ga-result-rank={index + 1}
                data-ga-target-id={post._sys.filename}
                {...sharedStudyAnalytics}
              >
                <div className="relative aspect-[1200/630] w-full overflow-hidden bg-slate-100">
                  {thumbnail ? (
                    <Image
                      src={thumbnail}
                      alt=""
                      fill
                      sizes="(max-width:640px) 85vw, (max-width:1024px) 40vw, 520px"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 group-focus-within:scale-105"
                      priority={isFeatured}
                      fetchPriority={isFeatured ? "high" : "auto"}
                    />
                  ) : null}
                </div>
                <div className="flex items-baseline gap-3 px-6 pb-5 pt-6 sm:px-7">
                  <span className="shrink-0 font-mono text-sm font-semibold tracking-[0.12em] text-blue-700">
                    {displayNumber}
                  </span>
                  <h3
                    lang={locale === "zh-Hans" ? "en" : undefined}
                    className="m-0 text-xl font-semibold leading-snug tracking-[-0.015em] text-slate-950 transition-colors group-hover:text-blue-700 sm:text-2xl"
                  >
                    {topicName}
                  </h3>
                </div>
              </Link>
              <nav
                aria-label={
                  locale === "zh-Hans"
                    ? `${topicName}：${h2NotesCopy.topicResources}`
                    : `${topicName} resources`
                }
                className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-5 py-4 sm:px-7"
              >
                <Link<string>
                  href={h2StudyRoute.noteHref as Route<string>}
                  data-ga-event="study_resource_selected"
                  data-ga-action="open_topic_notes"
                  data-ga-resource-type="notes"
                  data-ga-source-surface="h2_topic_card_actions"
                  data-ga-result-rank={index + 1}
                  data-ga-target-id={post._sys.filename}
                  {...sharedStudyAnalytics}
                  className="inline-flex min-h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-blue-700 px-3 py-2 text-sm font-semibold text-white no-underline transition hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 sm:gap-2 sm:px-4"
                >
                  <BookOpen className="h-4 w-4" aria-hidden="true" />
                  {locale === "zh-Hans" ? h2NotesCopy.notes : "Notes"}
                </Link>
                {h2StudyRoute.quizHref ? (
                  <Link<string>
                    href={h2StudyRoute.quizHref as Route<string>}
                    data-ga-event="study_resource_selected"
                    data-ga-action="open_topic_quiz"
                    data-ga-resource-type="quiz"
                    data-ga-source-surface="h2_topic_card_actions"
                    data-ga-result-rank={index + 1}
                    data-ga-target-id={h2StudyRoute.quizId ?? undefined}
                    {...sharedStudyAnalytics}
                    className="inline-flex min-h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 no-underline transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 sm:gap-2 sm:px-4"
                  >
                    <Target
                      className="h-4 w-4 text-blue-700"
                      aria-hidden="true"
                    />
                    {locale === "zh-Hans" ? h2NotesCopy.mcq : "MCQ"}
                  </Link>
                ) : null}
                {h2StudyRoute.videoHref ? (
                  <Link<string>
                    href={h2StudyRoute.videoHref as Route<string>}
                    data-ga-event="study_resource_selected"
                    data-ga-action="open_topic_video"
                    data-ga-resource-type="video"
                    data-ga-match-quality={
                      h2StudyRoute.videoMatchQuality ?? undefined
                    }
                    data-ga-source-surface="h2_topic_card_actions"
                    data-ga-result-rank={index + 1}
                    data-ga-target-id={h2StudyRoute.videoHref}
                    {...sharedStudyAnalytics}
                    className="inline-flex min-h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 no-underline transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 sm:gap-2 sm:px-4"
                  >
                    <PlayCircle
                      className="h-4 w-4 text-blue-700"
                      aria-hidden="true"
                    />
                    {locale === "zh-Hans"
                      ? h2StudyRoute.videoMatchQuality === "related"
                        ? h2NotesCopy.relatedVideo
                        : h2NotesCopy.video
                      : h2StudyRoute.videoMatchQuality === "related"
                      ? "Related video"
                      : "Video"}
                  </Link>
                ) : null}
              </nav>
            </li>
          );
        }
        const cardClassName =
          "relative flex h-full flex-col no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2";
        const cardBody = (
          <>
            {editorialFolio ? null : (
              <div className="relative aspect-[1200/630] w-full overflow-hidden bg-slate-100">
                {thumbnail && (
                  <Image
                    src={thumbnail}
                    alt={post.title ?? ""}
                    fill
                    sizes="(max-width:640px) 85vw,
                          (max-width:1024px) 40vw,
                          520px"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 group-focus-visible:scale-105"
                    priority={isFeatured}
                    fetchPriority={isFeatured ? "high" : "auto"}
                  />
                )}
              </div>
            )}
            <div
              className={clsx(
                "flex flex-1 flex-col gap-3",
                editorialFolio
                  ? "gap-4 px-0 pt-6"
                  : editorial
                  ? "p-6 sm:p-7"
                  : "p-6"
              )}
            >
              {postContext && (
                <span
                  className={clsx(
                    "flex self-start flex-col gap-1 font-semibold",
                    editorial
                      ? "text-blue-700"
                      : "text-xs uppercase tracking-wide text-slate-600"
                  )}
                >
                  <span
                    className={clsx(
                      editorial &&
                        "font-mono text-sm uppercase tracking-[0.16em]"
                    )}
                  >
                    {postContext.label}
                  </span>
                  {postContext.detail ? (
                    <span className="font-sans text-base tracking-normal text-slate-700">
                      {postContext.detail}
                    </span>
                  ) : null}
                </span>
              )}
              {!hideCategoryPill && post.category && (
                <span
                  className="self-start inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold transition group-hover:brightness-110 group-focus-visible:brightness-110"
                  style={pillStyle(post.category.color ?? "#2563eb")}
                >
                  {post.category.label}
                </span>
              )}
              {showLevelPill && post.level && (
                <span
                  className="self-start inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold transition group-hover:brightness-110 group-focus-visible:brightness-110"
                  style={pillStyle(
                    post.level === "upper-sec"
                      ? "#059669"
                      : post.level === "h2"
                      ? "#7c3aed"
                      : undefined
                  )}
                >
                  {post.level === "upper-sec"
                    ? "Upper Sec (IP/O‑Level)"
                    : post.level === "h2"
                    ? "H2 (A‑Level)"
                    : "Mixed"}
                </span>
              )}
              <h3
                className={clsx(
                  "mt-0 font-semibold leading-snug no-underline transition-colors duration-200 group-hover:text-blue-700 group-focus-visible:text-blue-700",
                  editorialFolio
                    ? "text-xl tracking-[-0.015em] sm:text-2xl"
                    : editorial
                    ? "text-2xl sm:text-[1.75rem]"
                    : "text-lg"
                )}
              >
                {post.title ?? post._sys.filename}
              </h3>
              {searchAnalytics && snippetText && (
                <div className="mt-1 space-y-2">
                  {matchedTerms.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {matchedTerms.map((term) => (
                        <span
                          key={`${post._sys.filename}-${term}`}
                          className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700"
                        >
                          {term}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="line-clamp-3 text-sm leading-relaxed text-slate-600">
                    {renderHighlightedSnippet({
                      explanationTerms,
                      text: snippetText,
                      queryTerms: searchExplanationTerms,
                    })}
                  </p>
                </div>
              )}
              {editorial || editorialFolio ? (
                <span
                  className="mt-auto pt-3 text-2xl leading-none text-blue-700 transition-transform duration-200 group-hover:translate-x-1 group-focus-within:translate-x-1"
                  aria-hidden="true"
                >
                  &#8594;
                </span>
              ) : null}
            </div>
          </>
        );
        return (
          <li
            key={post._sys.filename}
            className={clsx(
              "group relative overflow-hidden transition duration-300",
              editorialFolio
                ? "bg-transparent sm:pr-8"
                : editorial
                ? "rounded-2xl border border-slate-200 bg-white hover:-translate-y-1 hover:border-slate-300 hover:shadow-[0_24px_55px_rgba(15,23,42,0.12)] focus-within:-translate-y-1 focus-within:border-slate-300 focus-within:shadow-[0_24px_55px_rgba(15,23,42,0.12)]"
                : "rounded-2xl border border-white/60 bg-white/90 ring-1 ring-white/40 shadow-[0_18px_40px_rgba(15,23,42,0.1)] backdrop-blur-md hover:-translate-y-2 hover:shadow-[0_32px_70px_rgba(15,23,42,0.16)] focus-within:-translate-y-2 focus-within:shadow-[0_32px_70px_rgba(15,23,42,0.16)] before:absolute before:inset-x-8 before:top-0 before:h-px before:bg-white/80 before:content-['']"
            )}
          >
            {searchAnalytics ? (
              <SearchResultCardLink
                href={cardHref}
                className={cardClassName}
                action="open_search_result"
                label={post.category?.slug ?? undefined}
                queryLength={searchAnalytics.queryLength}
                resultRank={searchResultRank}
                searchBucket={searchAnalytics.searchBucket}
                source="results_grid"
                tool="blog_search"
                value={post._sys.filename}
                value2={
                  typeof post.level === "string" && post.level.length > 0
                    ? post.level
                    : undefined
                }
              >
                {cardBody}
              </SearchResultCardLink>
            ) : (
              <Link<string>
                href={cardHref as Route<string>}
                className={cardClassName}
                data-ga-event="content_click"
                data-ga-category="blog"
                data-ga-label={post.category?.slug ?? undefined}
                data-ga-action="open_post_card"
                data-ga-value={post._sys.filename}
                data-ga-element-id={`post_card_${post._sys.filename}`}
                data-ga-destination-path={cardHref}
                data-ga-impression="true"
                data-ga-impression-event="resource_impression"
                data-ga-value2={
                  typeof post.level === "string" && post.level.length > 0
                    ? post.level
                    : undefined
                }
              >
                {cardBody}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}
